import type { Messages } from './en';

/**
 * Fase E (paquete Xinyi): el distrito del Taipéi 101, Xinyi, la Montaña del Elefante y la zona de la Estación Central.
 * Las líneas de práctica de inglés se quedan en inglés a propósito.
 */
export const esXinyi: Pick<Messages, 'xinyi'> = {
  xinyi: {
    mapNames: {} as Record<string, string>,
    zones: {
      xy_xiangshan: 'Montaña del Elefante',
      xy_viewpoint: 'Mirador de la Montaña del Elefante',
      xy_sysHall: 'Salón Conmemorativo de Sun Yat-sen',
      xy_avenue: 'Avenida Zhongxiao Este',
      xy_arcades: 'Soportales con tiendas',
      xy_stationFront: 'Delante de la estación',
      xy_village: 'Aldea Cuatro Cuatro Sur',
      xy_xinyi: 'Zona comercial de Xinyi',
    },
    objects: {
      xy_taxi: 'Parada de taxis',
      xy_sysHall: 'Salón Conmemorativo de Sun Yat-sen',
      xy_cuihu: 'Estanque Cuihu',
      xy_mrtSys: 'MRT · Salón Conmemorativo de Sun Yat-sen',
      xy_mall101: 'Centro comercial Taipéi 101',
      xy_love: 'La escultura «愛»',
      xy_fireworks: 'Cartel de los fuegos de Año Nuevo',
      xy_conv: 'Tienda Buen Vecino',
      xy_tea: 'Tetería Bubble Bear',
      xy_pharmacy: 'Farmacia',
      xy_trash: 'Parada del camión de la basura',
      xy_shrine: 'Altar del Dios de la Tierra',
      xy_village: 'Aldea Cuatro Cuatro Sur',
      xy_bagel: 'Café Good Slope',
      xy_market1: 'Puesto del mercadillo: jabones y cestas',
      xy_market2: 'Puesto del mercadillo: plantitas',
      xy_market3: 'Puesto del mercadillo: postales',
      xy_dept: 'Grandes almacenes Galaxy',
      xy_books: 'Librería Book Tree',
      xy_screen: 'Pantalla gigante de la Plaza Moonlight',
      xy_stage: 'Rincón de artistas callejeros',
      xy_mrt101: 'MRT · Taipéi 101 / World Trade Center',
      xy_trailhead: 'Inicio del sendero de la Montaña del Elefante',
      xy_rocks: 'Las Seis Rocas Gigantes',
      xy_viewpoint: 'Mirador de la Montaña del Elefante',
      xy_easycard: 'Máquina para recargar la EasyCard',
      xy_priority: 'Asientos reservados',
    },
    npc: {
      xy_office1: 'Oficinista',
      xy_office2: 'Oficinista',
      xy_tourDad: 'Padre turista',
      xy_tourKid: 'Niña turista',
      xy_grandma: 'La abuela Lin',
      xy_hiker: 'Excursionista',
      xy_clerk: 'Librera',
      xy_teaSeller: 'Vendedor de té de burbujas',
      xy_trashLady: 'Vecina',
      xy_vendor: 'Artesana del mercadillo',
      xy_taichi: 'Abuelo del taichí',
      xy_taxiDriver: 'Taxista',
      xy_performer: 'Artista de globos',
    },
    items: {} as Record<string, { name: string; desc: string }>,

    common: {
      quiz: '📝 Test de inglés',
      right: '✅ ¡Muy bien!',
      wrong: '❌ Casi. ¡Inténtalo otra vez!',
      back: 'Quizá luego',
    },
    welcome: '¡Bienvenido a Taipéi! El Taipéi 101 está al este, pasado el Salón Conmemorativo. Detrás está la Montaña del Elefante.',

    sysHall: {
      text: 'El Salón Conmemorativo de Sun Yat-sen honra al doctor Sun Yat-sen, el padre fundador de la República de China. Mira el tejado amarillo dorado: sus esquinas se curvan hacia arriba como las alas de un pájaro. Dentro, la guardia de honor está inmóvil junto a una enorme estatua de bronce, y hace el relevo cada hora.',
      watch: '💂 Mirar a la guardia',
      watched: '¡Clac, clac! Los guardias marchan perfectamente sincronizados y luego se quedan quietos como estatuas.',
      q: 'Práctica de inglés: “What colour is the roof of the Memorial Hall?”', // English practice
      a: ['It is yellow.', 'It is green.', 'It is black.'], // English practice
      ok: '✅ ¡Sí! Tejas vidriadas amarillas que brillan al sol.',
    },
    cuihu: {
      text: 'Cuihu significa «lago verde». Carpas koi rojas, blancas y doradas nadan entre las hojas de loto, y las tortugas toman el sol en las rocas.',
      look: '🐟 Mirar los peces',
      say: 'Look! The fish are so colourful!', // English practice
      looked: 'Una koi naranja enorme abre la boca delante de ti. ¡Blub!',
    },
    mall: {
      text: 'El centro comercial Taipéi 101 ocupa las cinco primeras plantas de la torre: moda, un supermercado y una gran zona de restaurantes.',
      q: 'Práctica de inglés: preguntas “Excuse me, where is the food court?” y el guardia contesta. ¿Qué respuesta tiene sentido?', // English practice
      a: ['It is on the basement level, B1. Take the escalator down.', 'It is in my pocket.', 'It is on the moon, next to the cat.'], // English practice
      ok: '✅ ¡Correcto! En Taipéi las zonas de restaurantes suelen estar en el sótano.',
      browse: '👀 Mirar escaparates',
      browsed: 'Tiendas relucientes y aire acondicionado fresquito. Solo miras… ¡y es divertido!',
    },
    love: {
      text: 'En la plaza hay una escultura roja gigante del carácter 愛 («amor»). ¡Todo el mundo se hace una foto aquí!',
      ask: '📸 Pedir a alguien que te haga una foto',
      howAsk: '¿Cómo se pide con educación?',
      ok: 'Excuse me, could you take a photo of me, please?', // English practice
      bad: ['Photo. Now. You.', 'Give me your camera.'], // English practice
      done: '“Sure! Say cheese!” ¡Clic! Una foto preciosa con un gran 愛 rojo.',
    },
    fireworks: {
      text: 'Cada Nochevieja, a medianoche, salen fuegos artificiales de todas las plantas del Taipéi 101 durante unos cinco minutos. Cientos de miles de personas hacen la cuenta atrás juntas en Xinyi.',
      q: 'Práctica de inglés: “When do the fireworks start?”', // English practice
      a: ['At midnight, when the new year begins.', 'At lunchtime on Tuesdays.', 'Every morning at seven.'], // English practice
      ok: '✅ ¡Correcto! Diez, nueve, ocho… ¡Feliz Año Nuevo!',
      countdown: 'Ten, nine, eight, seven, six, five, four, three, two, one… Happy New Year!', // English practice
    },
    conv: {
      text: '¡Din-don! El timbre de la puerta de la tienda Buen Vecino. En Taiwán, las tiendas de conveniencia venden tentempiés, sirven para pagar recibos e imprimir documentos, y abren las 24 horas.',
      snack: '🥚 Comprar un huevo de té y agua',
      bag: '🛍️ «¿Necesitas bolsa?»',
      food: { vendor: 'Welcome! Anything else?', ok: 'One tea egg and a bottle of water, please.', bad: ['Egg. Water. Go.', 'I want the whole shop for free.'], done: 'Un huevo de té calentito que huele a salsa de soja y anís estrellado, y agua fresquita. ¡Perfecto!', price: 25 }, // English practice
      bagQ: 'Práctica de inglés: el dependiente pregunta “Do you need a bag?”. Tú ya llevas tu bolsa. ¿Qué dices?', // English practice
      bagA: ['No, thanks. I have one.', 'Bag is me.', 'Yes, I need ten bags for one egg.'], // English practice
      bagOk: '✅ ¡Genial! En Taiwán las bolsas de plástico se pagan, así que la gente lleva la suya.',
    },
    tea: {
      text: 'Bubble Bear 泡泡熊: ¡el té con leche y perlas se inventó en Taiwán! Tú eliges el azúcar (全糖 todo, 半糖 la mitad, 微糖 un poquito, 無糖 nada) y el hielo (少冰 poco hielo, 去冰 sin hielo).',
      menu: '¿Qué te apetece?',
      milk: '🧋 Té con leche y perlas',
      green: '🍵 Té verde de jazmín',
      milkOrder: { vendor: 'One pearl milk tea! How much sugar and ice?', ok: 'Half sugar, less ice, please.', bad: ['Sugar sugar ice ice!', 'Give me all the sugar and no cup.'], done: '¡Agita, agita y pop! Entra la pajita. Perlas blanditas y té cremoso, ¡y no demasiado dulce!', price: 50 }, // English practice
      greenOrder: { vendor: 'Jasmine green tea! Sugar and ice level?', ok: 'No sugar and no ice, please.', bad: ['Tea. Cold. Sugar? Maybe.', 'I want a hot ice tea.'], done: 'Suave, con aroma a flores y refrescante. ¡Uno de los favoritos de la gente de aquí!', price: 35 }, // English practice
    },
    pharmacy: {
      text: '藥局 significa farmacia. El farmacéutico te puede ayudar con problemas pequeños, como un resfriado o un dolor de cabeza.',
      q: 'Práctica de inglés: te duele la cabeza. ¿Qué preguntas?', // English practice
      a: ['Excuse me, do you have something for a headache?', 'My head is a banana.', 'Give me all the medicine.'], // English practice
      ok: '✅ Perfecto. El farmacéutico sonríe: “Drink water and get some rest!”',
    },
    trash: {
      text: 'En Taipéi casi no hay papeleras en la calle. Cada tarde llega el camión amarillo de la basura tocando música —hoy casi siempre «La oración de una virgen», de Bądarzewska (en algunos sitios aún suena «Para Elisa», de Beethoven)— y todo el mundo sale corriendo con sus bolsas. El reciclaje va en un segundo camión.',
      here: '🎵 ¡Ya está aquí el camión de la basura! Los vecinos salen deprisa con sus bolsas.',
      notYet: 'El camión pasa por aquí sobre las 8 de la tarde.',
      q: 'Práctica de inglés: ¿qué dices cuando sales con las bolsas?', // English practice
      a: ['I am taking out the trash!', 'I am taking out the dog’s homework!', 'I am eating the trash!'], // English practice
      ok: '✅ ¡Sí! Y no te olvides de separar el reciclaje.',
      toast: '🎵 Ta-ra-ra-ra… ¡«La oración de una virgen»! El camión de la basura viene calle abajo.',
    },
    shrine: {
      text: 'Un altar pequeñito para Tudigong (土地公), el Dios de la Tierra que protege el barrio. La gente hace reverencias con incienso y le deja fruta, té o galletas.',
      bow: '🙏 Hacer tres reverencias',
      wish: 'Please keep my family safe and healthy.', // English practice
      bowed: 'Haces tres reverencias. El humo del incienso sube enroscándose hacia el baniano.',
    },
    village: {
      text: 'La Aldea Cuatro Cuatro Sur (四四南村) se construyó en 1948 para las familias de los trabajadores del arsenal número 44. Las casitas bajas de ladrillo gris son ahora un pequeño museo, tiendas y un café, justo debajo del Taipéi 101: lo viejo y lo nuevo, uno al lado del otro.',
      q: 'Práctica de inglés: “Who lived in these houses in the old days?”', // English practice
      a: ['Military families.', 'Pirates.', 'Astronauts.'], // English practice
      ok: '✅ ¡Correcto! Había aldeas de familias de militares (眷村) por todo Taiwán.',
      market: 'Cada domingo el césped se convierte en el Simple Market: artesanía, plantas y tentempiés.',
      noMarket: 'El Simple Market solo se hace los domingos. ¡Vuelve entonces!',
    },
    bagel: { vendor: 'Hi! Our bagels are fresh from the oven. What would you like?', ok: 'Could I have a sesame bagel and a cup of tea, please?', bad: ['Bagel! Hurry!', 'I want a bagel made of tea.'], done: 'Un bagel de sésamo calentito y esponjoso, y un té, en una casa antigua de la aldea. ¡Qué a gusto!', price: 45 }, // English practice
    market: {
      ask: 'How much is this, please?', // English practice
      stalls: {
        xy_market1: { text: 'Jabón artesano que huele a pomelo y cestitas tejidas.', answer: 'It is thirty dollars.', price: 30 }, // English practice
        xy_market2: { text: 'Suculentas diminutas en tazas de té y macetas hechas con latas viejas.', answer: 'It is twenty dollars.', price: 20 }, // English practice
        xy_market3: { text: 'Postales dibujadas a mano del Taipéi 101, el MRT y los gatos callejeros.', answer: 'It is fifteen dollars.', price: 15 }, // English practice
      },
      buy: (n: number) => `🛍️ Comprarlo (${n} monedas)`,
      bought: 'Has comprado algo hecho a mano. La artesana te lo envuelve en papel de periódico con una sonrisa.',
      poor: 'No tienes suficientes monedas.',
    },
    dept: {
      text: 'Grandes almacenes Galaxy: diez plantas de tiendas, unidas a los edificios vecinos por pasarelas elevadas. En otoño todos los almacenes hacen unas rebajas de aniversario enormes (週年慶).',
      q: 'Práctica de inglés: te gusta una camiseta. ¿Qué le preguntas a la dependienta?', // English practice
      a: ['Excuse me, can I try this on?', 'T-shirt! Me! Now!', 'How old is this shirt?'], // English practice
      ok: '✅ “Of course! The fitting room is over there.”',
    },
    books: {
      text: 'Book Tree 書林: una librería famosa donde la gente se sienta en el suelo a leer durante horas. Muchas plantas de libros, música, papelería y una cafetería.',
      read: '📖 Leer un rato',
      readDone: 'Lees un libro ilustrado en inglés sobre un gato que viaja en el MRT. ¡El tiempo vuela!',
      askClerk: '🗣️ Preguntar por los libros en inglés',
      clerkSays: 'La librera dice: “English books? They are on the second floor, next to the stairs.”', // English practice
      q: '¿Dónde están los libros en inglés?',
      a: ['On the second floor, next to the stairs.', 'In the basement, next to the café.', 'On the roof.'], // English practice
      ok: '✅ ¡Muy bien escuchado! Escaleras arriba.',
      closed: 'Book Tree está cerrada ahora. Abre a las 10 de la mañana.',
    },
    screen: {
      text: 'La pantalla gigante de la Plaza Moonlight pone anuncios, videoclips y curiosidades de la ciudad.',
      q: 'Práctica de inglés: la pantalla pregunta “How tall is Taipei 101?”', // English practice
      a: ['It is 508 metres tall.', 'It is 50 metres tall.', 'It is 5,000 metres tall.'], // English practice
      ok: '✅ ¡Correcto! Fue el edificio más alto del mundo de 2004 a 2010.',
    },
    stage: {
      text: 'Un rincón para artistas callejeros. En Taipéi necesitan una licencia de artista callejero, y actúan aquí todas las tardes.',
      watch: '🎶 Ver el espectáculo',
      watched: 'Música, globos y aplausos. ¡El público lo celebra!',
      tip: '🪙 Dar 5 monedas de propina',
      thanks: 'That was amazing! Thank you!', // English practice
      empty: 'Ahora no actúa nadie. Vuelve por la tarde o por la noche.',
    },
    trail: {
      text: 'Sendero de la Montaña del Elefante (象山步道): unos 20 minutos de escalones de piedra empinados hasta el mirador sobre el Taipéi 101. ¡Lleva agua y descansa cuando lo necesites!',
      q: 'Práctica de inglés: “How long does it take to get to the top?”', // English practice
      a: ['About twenty minutes.', 'About twenty days.', 'Twenty seconds.'], // English practice
      ok: '✅ ¡Correcto! Vamos allá: escalón a escalón.',
      tired: '¡Uf, cuántos escalones! (−energía)',
    },
    rocks: {
      text: 'Las Seis Rocas Gigantes (六巨石): seis rocas enormes cerca de la cima de la Montaña del Elefante. Los excursionistas se suben para hacerse una foto famosa.',
      climb: '🧗 Subir a una roca',
      careful: 'Be careful! Hold on tight.', // English practice
      climbed: 'Subes con cuidado y saludas desde arriba. ¡La ciudad brilla a tus pies!',
    },
    viewpoint: {
      text: 'Esta es la vista clásica: el Taipéi 101 alzándose sobre toda la ciudad.',
      shoot: '📸 Hacer la foto',
      say: 'What a view! Taipei 101 looks so tall from here.', // English practice
      title: '📸 Montaña del Elefante',
      caption: 'Tu foto del Taipéi 101 desde la Montaña del Elefante.',
      tap: 'Toca para cerrar',
    },
    taxi: {
      text: 'En Taipéi hay taxis amarillos por todas partes. El taxista pregunta: “Where to?”', // English practice
      to: {
        station: 'To Taipei Main Station, please.',
        tower: 'To Taipei 101, please.',
        village: 'To Four Four South Village, please.',
        trail: 'To the Elephant Mountain trailhead, please.',
      }, // English practice
      price: (n: number) => `${n} monedas`,
      poor: 'Necesitas más monedas para el taxi.',
      ride: '🚕 ¡En marcha! “Please fasten your seat belt.”', // English practice
    },
    easycard: {
      text: 'La máquina de la EasyCard: pon la tarjeta en el lector, elige una cantidad y mete el dinero. La EasyCard sirve para el MRT, los autobuses, las YouBike y las tiendas de conveniencia.',
      top: '💳 Recargar mi tarjeta',
      q: 'Práctica de inglés: la pantalla dice “Please place your card on the reader.” ¿Qué haces primero?', // English practice
      a: ['I put my card on the reader.', 'I put my card in my mouth.', 'I shout at the machine.'], // English practice
      q2: 'Práctica de inglés: “How much would you like to add?”', // English practice
      a2: ['One hundred dollars, please.', 'My name is one hundred.', 'Yes, I would like a train.'], // English practice
      ok: '✅ ¡Pip! Tarjeta recargada. El saldo aparece en la pantalla.',
    },
    priority: {
      text: 'Los asientos azul oscuro son asientos reservados (博愛座). Cualquiera puede sentarse, pero se los cedes a quien los necesite más.',
      q: 'Práctica de inglés: sube al tren un señor mayor. ¿Qué le dices?', // English practice
      a: ['Please, take my seat.', 'This is my seat forever.', 'Go away, please.'], // English practice
      ok: '✅ ¡Qué amable! Él te dice: “Thank you, young friend.”',
    },

    talk: {
      guideTip: 'Consejo: los taxis amarillos esperan junto a la estación. El Taipéi 101 está al este, ¡y la Montaña del Elefante justo detrás!',
      office1: [
        '¡Hora de comer! A mediodía la zona de restaurantes del sótano está llenísima.',
        'Trabajo en la planta 35 de una torre de por ahí. ¡Mi ascensor no es tan rápido como el del 101!',
        'Práctica de inglés: “I have a meeting at two.” “See you later!”', // English practice
      ],
      office2: [
        'Voy en MRT todos los días. ¡Paso la EasyCard y listo!',
        'Al salir del trabajo me gusta ir por las pasarelas elevadas. ¡No tienes que cruzar ni una calle!',
        'Práctica de inglés: “Excuse me, is this seat taken?” “No, go ahead.”', // English practice
      ],
      tourAsk: 'Excuse me! We are lost. How do we get to Taipei 101?', // English practice
      tourQ: '¿Cómo se dan indicaciones?',
      tourA: ['Go straight along this road, then look for the tallest tower. You can’t miss it!', 'Taipei 101 is in my bag.', 'Turn left, turn left, turn left, turn left.'], // English practice
      tourOk: '“Thank you so much! You are very kind.” La familia se despide con la mano y se va hacia el este.',
      tourKid: ['¡Me encantan las motos! ¡Hay muchísimas!', '¡Mi padre dice que luego tomaremos té de burbujas!', 'Práctica de inglés: “What is your name?” “My name is Lily. Nice to meet you!”'], // English practice
      grandmaShrine: [
        'Vengo a ver a Tudigong cada mañana. Él protege nuestra calle.',
        'Tres reverencias, así. Despacito, despacito.',
        'Práctica de inglés: “Good morning, Grandma!” ¡Dímelo a mí!', // English practice
      ],
      grandmaMarket: [
        '¡Ay, el mercadillo del domingo! Siempre me compro una plantita.',
        'Yo me crie en una aldea como esta. Lo compartíamos todo con los vecinos.',
      ],
      hiker: [
        '¡Sigue! La vista de arriba vale cada escalón.',
        'Práctica de inglés: “I am out of breath!” “Let’s take a short break.”', // English practice
        'Al atardecer se ilumina el 101. ¡Quédate a ver la vista nocturna!',
      ],
      clerk: [
        'Bienvenido a Book Tree. Puedes leer lo que quieras, sin prisa.',
        'La sección de inglés está en la segunda planta, al lado de las escaleras.',
        'Práctica de inglés: “Could you recommend a good book?”', // English practice
      ],
      trashLady: [
        '¡Corre, corre! ¡Cuando oyes «La oración de una virgen», es que viene el camión!',
        'La basura, al camión amarillo; el reciclaje, al blanco.',
        'Práctica de inglés: “Don’t forget to take out the trash!”', // English practice
      ],
      vendor: [
        '¡Todo lo que hay aquí lo he hecho yo a mano!',
        'Práctica de inglés: “How much is this?” “It is twenty dollars.”', // English practice
      ],
      taichi: [
        'Despacio… inspira… espira. ¡El taichí me mantiene joven!',
        'Tengo ochenta y dos años. Vengo aquí cada mañana.',
        'Práctica de inglés: “Breathe in. Breathe out. Relax.”', // English practice
      ],
      taxiDriver: [
        '¿Adónde vamos? ¡Me conozco todas las calles de Taipéi!',
        'Práctica de inglés: “Please fasten your seat belt.”', // English practice
      ],
      performer: [
        '¡Un globo para ti! ¿Lo quieres de perro o de jirafa?',
        'Práctica de inglés: “That was amazing!”: eso es lo que dice el público.', // English practice
      ],
      teaSeller: '¡Bienvenido a Bubble Bear! ¿Cuánto azúcar? ¿Cuánto hielo?',
    },
  },
};
